/*
 * High-Risk Users page.
 *
 * Full user roster with funnel counts and the overall risk score,
 * paginated and sortable on every column (default: click rate DESC).
 * Inherited scope applies; export via the table's own export menu.
 *
 * Calculations:
 *   - clickRate = clicked / sent; reportRate = reported / sent.
 *   - sent / clicked / 2nd stage / reported follow the selected period.
 *   - risk = the user's overall 0-100 risk score (fixed 180-day risk_snapshot).
 *
 * Integration hook: useClickUsers (user_roster, windowed, limit 5000).
 */
import React, { useMemo } from 'react';
import { Box } from '@mui/material';
import { motion } from 'motion/react';
import { USERS, SEND_LOG, DATA_EPOCH } from '../data';
import { logInPeriod } from '../hooks/useDashboardData';
import { useClickUsers, buildClickUserStats } from '../clickUsers';
import ActiveFilters from './ActiveFilters';
import DataTable from './DataTable';

const isIn = (val, vs) => { if (!vs) return true; const a = Array.isArray(val) ? val : [val]; return Array.isArray(vs) ? a.some((x) => vs.includes(x)) : a.includes(vs); };

function applyScope(log, scope) {
    const s = scope || {};
    return log.filter((e) => {
        if (!isIn(e.dept,     s.department)) return false;
        if (!isIn(e.location, s.location))   return false;
        if (!isIn(e.city,     s.city))       return false;
        if (!isIn(e.country,  s.country))    return false;
        if (!isIn(e.company,  s.company))    return false;
        if (!isIn(e.state,    s.state))      return false;
        if (!isIn(e.adGroups, s.adGroup))    return false;
        if (s.user) {
            const vs = Array.isArray(s.user) ? s.user : [s.user];
            if (!vs.some((v) => e.email === v || `${e.firstName} ${e.lastName}` === v)) return false;
        }
        return true;
    });
}

const fadeUp = { hidden: { opacity: 0, y: 12 }, visible: { opacity: 1, y: 0, transition: { duration: 0.3, ease: 'easeOut' } } };
const stagger = { hidden: {}, visible: { transition: { staggerChildren: 0.07 } } };

// Canonical High Click Users columns, shared with the Users card on the
// Simulations page so the preview and the drill can't drift apart.
export function highClickUserColumns() {
    return [
        { id: 'name',       label: 'Name',       always: true,   sortable: true, type: 'string', accessor: (u) => u.name },
        { id: 'email',      label: 'Email',      defaultOn: true,sortable: true, type: 'string', accessor: (u) => u.email },
        { id: 'risk',       label: 'Risk Score', defaultOn: true,sortable: true, type: 'number', accessor: (u) => u.risk ?? -1, align: 'right',
          render: (u) => u.risk == null
              ? <Box component="span" sx={{ color: 'text.disabled', fontWeight: 500 }}>Unscored</Box>
              : <Box component="span" sx={{ fontWeight: 700, color: u.risk >= 56 ? 'var(--red)' : u.risk >= 30 ? 'var(--amber)' : 'var(--green)' }}>{u.risk}</Box> },
        { id: 'sent',       label: 'Sent',       defaultOn: true,sortable: true, type: 'number', accessor: (u) => u.sent, align: 'right' },
        { id: 'clicked',    label: 'Clicked',    defaultOn: true,sortable: true, type: 'number', accessor: (u) => u.clicked, align: 'right',
          render: (u) => <Box sx={{ fontWeight: 700, color: u.clicked > 2 ? 'var(--red)' : u.clicked > 0 ? 'var(--amber)' : 'var(--green)' }}>{u.clicked}</Box> },
        { id: 'secondStage',label: '2nd Stage',  defaultOn: true,sortable: true, type: 'number', accessor: (u) => u.secondStage, align: 'right',
          render: (u) => <Box sx={{ fontWeight: 700, color: u.secondStage > 0 ? 'var(--red)' : 'text.secondary' }}>{u.secondStage}</Box> },
        { id: 'reported',   label: 'Reported',   defaultOn: true,sortable: true, type: 'number', accessor: (u) => u.reported, align: 'right',
          render: (u) => <Box sx={{ fontWeight: 700, color: u.reported > 0 ? 'var(--green)' : 'text.secondary' }}>{u.reported}</Box> },
        { id: 'clickRate',  label: 'Click Rate', defaultOn: true,sortable: true, type: 'number', accessor: (u) => u.clickRate, align: 'right',
          render: (u) => <Box sx={{ fontWeight: 600, color: u.clickRate >= 30 ? 'var(--red)' : u.clickRate >= 15 ? 'var(--amber)' : 'var(--green)' }}>{u.clickRate}%</Box> },
        { id: 'reportRate', label: 'Report Rate',defaultOn: true,sortable: true, type: 'number', accessor: (u) => u.reportRate, align: 'right',
          render: (u) => <Box sx={{ fontWeight: 600, color: u.reportRate >= 15 ? 'var(--green)' : 'text.secondary' }}>{u.reportRate}%</Box> },
    ];
}

export default function HighRiskUsersPage({ drill, scope, removeScope, period, startDate, endDate }) {
    // Funnel counts follow the selected period (BD-1003) via the windowed user_roster;
    // the Risk Score column stays the fixed 180-day snapshot. Until the roster arrives
    // the local send log (windowed + scoped) fills in.
    const serverRows = useClickUsers({ scope, period, startDate, endDate });
    const rows = useMemo(() => {
        if (serverRows) return serverRows;
        const riskByEmail = new Map(USERS.map((u) => [u.email, u.risk]));
        return buildClickUserStats(applyScope(logInPeriod(SEND_LOG, { period, startDate, endDate }), scope), riskByEmail);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [serverRows, scope, period, startDate, endDate, DATA_EPOCH]);

    const columns = highClickUserColumns();

    return (
        <motion.div variants={stagger} initial="hidden" animate="visible" style={{ padding: '0 24px 48px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <ActiveFilters scope={scope} removeScope={removeScope} noPadX />
            <motion.div variants={fadeUp}>
                <DataTable
                    title="High Click Users"
                    subtitle={`${rows.length.toLocaleString()} users`}
                    columns={columns}
                    rows={rows}
                    rowKey={(u) => u.email || u.name}
                    onRowClick={(u) => drill('user-detail', u.name, { type: 'user', value: u.email || u.name })}
                    initialSort={{ by: 'clickRate', dir: 'desc' }}
                    storageKey="rbx-high-risk-cols-v2"
                    emptyMessage="No users in scope"
                />
            </motion.div>
        </motion.div>
    );
}
